import type { AgentCorePageContext } from '@/shared/api/agentCore/agentCoreApi';

import type { AgentPromptPage } from '../config';

// Report page flavors. Path shapes mirror lib/pageContext.ts route rules:
// `/patients/:patientID/reports/<kind>/<reportID>` for kind-first reports,
// `/patients/:patientID/reports/:reportID/<section>` for section views.
type ReportFlavorRule = {
  pattern: RegExp;
  page: AgentPromptPage;
};

// Ordered most-specific-first. `preview` sections and bare report routes
// fall through to the generic report flavor.
const REPORT_FLAVOR_RULES: ReportFlavorRule[] = [
  { pattern: /\/reports\/endodontic\//, page: 'report-endo' },
  { pattern: /\/reports\/implant\//, page: 'report-implant' },
  { pattern: /\/reports\/molar\//, page: 'report-molar' },
  { pattern: /\/reports\/segmentation\//, page: 'report-segmentation' },
  { pattern: /\/reports\/[^/]+\/gp(?:\/|$)/, page: 'report-cbct' },
  { pattern: /\/reports\/[^/]+\/pano(?:\/|$)/, page: 'report-pano' },
  { pattern: /\/reports\/[^/]+\/bitewings(?:\/|$)/, page: 'report-bitewings' },
  { pattern: /\/reports\/[^/]+\/ioxray(?:\/|$)/, page: 'report-ioxray' },
];

/**
 * Maps the chat's page context to the library page flavor. Absent context
 * keeps the historical default: the patient-page preset set.
 */
export const resolvePromptPage = (
  pageContext?: Pick<AgentCorePageContext, 'page' | 'path'>,
): AgentPromptPage => {
  if (!pageContext) {
    return 'patient';
  }

  const { page, path = '' } = pageContext;

  switch (page) {
    case 'patient':
      return 'patient';
    case 'patient-consultation':
      return 'consultation';
    case 'patient-dental-photos':
      return 'photos';
    case 'patient-document':
      return 'document';
    case 'encounter':
    case 'encounters':
      return 'encounter';
    case 'transcriptor-templates':
    case 'company-settings':
      return 'templates';
    case 'patient-report': {
      if (path.includes('/preview2d/')) {
        return 'report';
      }
      const rule = REPORT_FLAVOR_RULES.find(({ pattern }) =>
        pattern.test(path),
      );
      return rule ? rule.page : 'report';
    }
    default:
      return 'general';
  }
};
